import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Badge } from "../../components/ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { Label } from "../../components/ui/label"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu"
import { Textarea } from "../../components/ui/textarea"
import { cn } from "../../components/lib/utils"
import { motion, useAnimation } from "framer-motion"
import {
  Search,
  ChevronDown,
  ChevronUp,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CheckCircle,
  Building,
  GraduationCap,
  Languages,
  Briefcase,
  Award,
  CreditCard,
  Baby,
  ArrowLeftCircle,
  Clock,
  RefreshCw,
  Link,
  Smartphone,
  Globe,
  History,
  Info,
  IdCard,
  Loader2,
  Pencil,
} from "lucide-react"
import {
  getAllApplications,
  getCandidateById,
  updateApplicationStatus,
  reassignCandidateApplication,
  updateJoiningDate,
  updateExpectedJoiningDate,
  updateCandidateAdditionalDetailsForReassign,
  updateCandidateContactDetails,
  editApplication,
} from "../../services/candidateapi"
import {
  scheduleInterviewRound,
  updateInterviewRoundStatus,
  getInterviewDetailsByApplicationId,
  rescheduleInterviewRound,
  type InterviewDetailsResponse,
} from "../../services/interviewapi"
import { toast } from "react-hot-toast"
import { getJobs, getJobDetailsById } from "../../services/jobsapi"
import { useRbac } from "../../context"
import { getAllUsers, getMySubordinates } from "../../services/usersapi"
import "react-datepicker/dist/react-datepicker.css"
import "react-time-picker/dist/TimePicker.css"
import "react-clock/dist/Clock.css"
import type {
  Application,
  PaginationState,
  ScheduleFormData,
  ResultFormData,
  UserType,
  ValidationErrors,
} from "../../components/types/application.types"
import { datePickerStyles } from "../../components/constants/styles"
import {
  getStatusBadgeColor,
  getInterviewDate,
  showConfirmation,
  formatTimeTo12Hour,
  getStatusChangeOptions,
  getDefaultStatusChangeValue,
  isNotSelectedOnlyStatusChange,
  APPLICATION_STATUS_FILTER_OPTIONS,
  isInterviewTodayStatusFilter,
} from "../../components/utils/application.utils"
import { InterviewDetailsModal } from "../../components/InterviewDetailsModal"
import { ResultModal } from "../../components/ResultModal"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select"
import { fetchApplicationsExportBatch, getUsers, type ExportApplicationsResponse } from "../../services/apiclient";
import { uploadFile } from "../../services/usersapi";
import * as XLSX from "xlsx"
import { format } from "date-fns"

// Companies that require extra mandatory candidate fields (same as add-candidate form)
const SPECIAL_COMPANIES = (process.env.REACT_APP_COMPANY_REQUIRE_MANDATORY_FIELDS?.split(",") || []).map((s) => s.trim()).filter(Boolean)

function isJobFromSpecialCompany(jobs: any[], jobId: string): boolean {
  if (!jobId || !SPECIAL_COMPANIES.length) return false
  const job = jobs.find((j) => j.id === jobId || j._id === jobId)
  return !!job?.companyName && SPECIAL_COMPANIES.includes(job.companyName)
}

const CHANGE_STATUS_ENABLED_STATUSES = ["Selected", "Offered", "Interview Attended"]
const CHANGE_COMPANY_BLOCKED_STATUSES: string[] = []

const REASSIGN_FIELD_KEYS = [
  "alternativePhoneNumber",
  "maritalStatus",
  "howOftenAbleToJoin",
  "aadharNumber",
  "resumeUrl",
  "street",
  "area",
  "pincode",
] as const
type ReassignFieldKey = (typeof REASSIGN_FIELD_KEYS)[number]

const REASSIGN_FIELD_LABELS: Record<ReassignFieldKey, string> = {
  alternativePhoneNumber: "Alternative phone number",
  maritalStatus: "Marital status",
  howOftenAbleToJoin: "How often able to join",
  aadharNumber: "Aadhar number",
  resumeUrl: "Resume (URL or upload)",
  street: "Street address",
  area: "Area/Locality",
  pincode: "Pincode",
}

// Simple validators mirroring add-candidate form rules
const validatePhone10 = (phone: string): boolean => /^\d{10}$/.test(phone.trim())
const validateAadhar12 = (aadhar: string): boolean => /^\d{12}$/.test(aadhar.trim())
const validatePincode6 = (pincode: string): boolean => /^\d{6}$/.test(pincode.trim())

const toDisplayText = (value: unknown): string => {
  if (value === null || value === undefined) return "N/A"
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  if (typeof value === "object") {
    const maybeLocation = value as { fullAddress?: unknown; city?: unknown; name?: unknown }
    if (typeof maybeLocation.fullAddress === "string" && maybeLocation.fullAddress.trim()) return maybeLocation.fullAddress
    if (typeof maybeLocation.city === "string" && maybeLocation.city.trim()) return maybeLocation.city
    if (typeof maybeLocation.name === "string" && maybeLocation.name.trim()) return maybeLocation.name
  }
  return "N/A"
}

/** Returns field keys that are missing on the candidate for special-company reassign. */
function getMissingMandatoryFieldsForReassign(candidate: any): ReassignFieldKey[] {
  const missing: ReassignFieldKey[] = []
  const altPhone = candidate?.additionalDetails?.alternateMobileNumber ?? candidate?.alternativePhoneNumber
  if (!altPhone || !String(altPhone).trim()) missing.push("alternativePhoneNumber")
  const marital = candidate?.maritalStatus
  if (!marital || !String(marital).trim()) missing.push("maritalStatus")
  const howOften = candidate?.howOftenAbleToJoin
  if (!howOften || !String(howOften).trim()) missing.push("howOftenAbleToJoin")
  const aadhar = candidate?.aadharNumber
  if (!aadhar || !String(aadhar).trim()) missing.push("aadharNumber")
  const resume = candidate?.resumeUrl ?? candidate?.resume
  if (!resume || !String(resume).trim()) missing.push("resumeUrl")
  const addr = candidate?.additionalDetails?.currentAddress ?? candidate?.currentLocationDetails
  if (!addr?.street || !String(addr.street).trim()) missing.push("street")
  if (!addr?.area || !String(addr.area).trim()) missing.push("area")
  if (!addr?.pincode || !String(addr.pincode).trim()) missing.push("pincode")
  return missing
}

interface Candidate {
  additionalDetails: {
    alternateMobileNumber?: string
    vendorName?: string
    region?: string
    panNumber?: string
    previousExperience?: string
    qualification?: string
    permanentAddress: {
      street?: string
      area?: string
      city?: string
      pincode?: string
      state?: string
      fullAddress?: string
    }
    currentAddress: {
      street?: string
      area?: string
      city?: string
      state?: string
      pincode?: string
      fullAddress?: string
    }
  }
  id: string
  name: string
  email: string
  phone: string
  highestQualification: string
  totalExperienceYears: number
  totalExperienceMonths: number
  skills: string[]
  currentLocationDetails: {
    city: string
    fullAddress: string
  }
  profileSource: string
  createdAt: string
  candidateProfileStrength: number
  fatherName?: string
  gender?: string
  dateOfBirth?: string
  aadharNumber?: string
  jobCategory?: string
  employmentType?: string
  spokenLanguages?: string[]
  preferredJobCategories?: string[]
  preferredEmploymentTypes?: string[]
}

export function ApplicationsList() {
  const navigate = useNavigate()
  const { user } = useRbac()
  const controls = useAnimation()

  // Inject custom styles for DatePicker
  useEffect(() => {
    if (typeof document !== "undefined" && datePickerStyles) {
      const styleSheet = document.createElement("style")
      styleSheet.innerText = datePickerStyles
      document.head.appendChild(styleSheet)
      return () => {
        if (document.head.contains(styleSheet)) {
          document.head.removeChild(styleSheet)
        }
      }
    }
  }, [])


  // State management
  const [applications, setApplications] = useState<Application[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  })
  const [searchTerm, setSearchTerm] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [selectedJob, setSelectedJob] = useState("all")
  const [selectedStatus, setSelectedStatus] = useState("all")
  const [selectedRecruiter, setSelectedRecruiter] = useState("all")
  const [selectedSubordinate, setSelectedSubordinate] = useState("all")
  const [selectedDateFrom, setSelectedDateFrom] = useState("")
  const [selectedDateTo, setSelectedDateTo] = useState("")
  const [showInterviewModal, setShowInterviewModal] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null)
  const [interviewDetails, setInterviewDetails] = useState<InterviewDetailsResponse | null>(null)
  const [showCandidateDetailsModal, setShowCandidateDetailsModal] = useState(false)
  const [selectedCandidateForDetails, setSelectedCandidateForDetails] = useState<Candidate | null>(null)
  const [selectedApplicationForDetails, setSelectedApplicationForDetails] = useState<Application | null>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [detailsError, setDetailsError] = useState<string | null>(null)
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false)
  const [showJoiningDateUpdateModal, setShowJoiningDateUpdateModal] = useState(false)
  const [showExpectedJoiningDateUpdateModal, setShowExpectedJoiningDateUpdateModal] = useState(false)
  const [showDateSelect, setShowDateSelect] = useState(false)
  const [joiningDate, setJoiningDate] = useState(new Date().toISOString().split("T")[0])
  const [expectedJoiningDate, setExpectedJoiningDate] = useState(new Date().toISOString().split("T")[0])
  const [statusUpdateData, setStatusUpdateData] = useState<{
    applicationId: string
    newStatus: string
    comments: string
    selectedJobId?: string
    currentStatus?: string
  }>({
    applicationId: "",
    newStatus: "",
    comments: "",
    selectedJobId: "",
    currentStatus: "",
  })
  const [sortBy, setSortBy] = useState("name")
  const [showLoaderInInterviewPopup, setShowLoaderInInterviewPopup] = useState(false)
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc")

  // Interview flow state
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [showScheduleForm, setShowScheduleForm] = useState(false)
  const [showResultModal, setShowResultModal] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)
  const [isSubmittingResult, setIsSubmittingResult] = useState(false)
  const [scheduleFormData, setScheduleFormData] = useState<ScheduleFormData>({
    interviewType: "Offline",
    scheduledDate: "",
    startTime: "",
    endTime: "",
    timeZone: "UTC",
    interviewers: [],
    notes: "",
    additionalParticipants: [],
    location: "",
  })
  const [resultFormData, setResultFormData] = useState<ResultFormData>({
    result: "Qualified",
    reason: "",
    isFinalRound: false,
    expectedJoiningDate: "",
  })

  // Validation and other state
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    startTime: false,
    endTime: false,
    scheduledDate: false,
  })
  const [currentRoundForAction, setCurrentRoundForAction] = useState<any>(null)
  const [jobs, setJobs] = useState<any[]>([])
  const [recruiters, setRecruiters] = useState<UserType[]>([])
  const [subordinates, setSubordinates] = useState<UserType[]>([])
  const [isLoadingJobs, setIsLoadingJobs] = useState(false)
  const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false)
  const [isLoadingSubordinates, setIsLoadingSubordinates] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)
  const [jobSearchTerm, setJobSearchTerm] = useState("")
  const [emailInput, setEmailInput] = useState<string>("")

  const [showEditCandidateModal, setShowEditCandidateModal] = useState(false)
  const [editCandidateApp, setEditCandidateApp] = useState<Application | null>(null)
  const [editCandidateForm, setEditCandidateForm] = useState({ name: "", email: "", phone: "" })
  const [editCandidateFieldErrors, setEditCandidateFieldErrors] = useState({
    name: "",
    email: "",
    phone: "",
  })
  const [editCandidateSaving, setEditCandidateSaving] = useState(false)
  const fetchIdRef = useRef(0)
  const [isReassigning, setIsReassigning] = useState(false)
  const [reassignMissingFields, setReassignMissingFields] = useState<ReassignFieldKey[]>([])
  const [candidateIdForReassign, setCandidateIdForReassign] = useState<string>("")
  const [reassignFormValues, setReassignFormValues] = useState<Record<string, string>>({})
  const [reassignFieldErrors, setReassignFieldErrors] = useState<Record<string, string>>({})
  const [reassignSaving, setReassignSaving] = useState(false)
  const [showChangeCompanyModal, setShowChangeCompanyModal] = useState(false)
  const [changeCompanyData, setChangeCompanyData] = useState({
    applicationId: "",
    selectedJobId: "",
    selectedJobLabel: "",
    comments: "",
    candidateName: "",
    currentCompany: "",
  })
  const [isChangingCompany, setIsChangingCompany] = useState(false)

  // Role-based filter configuration
  const allowedRecruiterRoles = useMemo(() => {
    if (user?.role === "ADMIN") return ["FBDE", "CONSULTANT", "SHM"]
    if (user?.role === "SHM") return ["HM", "AGENCY", "HR"]
    if (user?.role === "FBDE" || user?.role === "CONSULTANT") return ["HM", "AGENCY", "HR"]
    if (user?.role === "HM" || user?.role === "AGENCY") return ["HR"]
    return []
  }, [user?.role])

  // Prepare options for searchable selects
  useEffect(() => {
    // Radix Dialog can leave `pointer-events: none` on <body> after close; ensure we always clean up
    // when all modals that can trigger it are closed.
    if (!showStatusUpdateModal && !showJoiningDateUpdateModal && !showExpectedJoiningDateUpdateModal && !showEditCandidateModal && !showChangeCompanyModal) {
      setTimeout(() => {
        document.body.style.removeProperty("pointer-events")
        document.body.removeAttribute("data-scroll-locked")
      }, 0)
    }
  }, [showStatusUpdateModal, showJoiningDateUpdateModal, showExpectedJoiningDateUpdateModal, showEditCandidateModal, showChangeCompanyModal])


  useEffect(() => {
    if (statusUpdateData.newStatus === "Joined") {
      setShowDateSelect(true)
    } else {
      setJoiningDate(new Date().toISOString().split("T")[0])
      setShowDateSelect(false)
    }
  }, [statusUpdateData.newStatus])

  const recruiterOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Recruiters" }]
    if (recruiters.length > 0) {
      recruiters
        .filter((recruiter) => allowedRecruiterRoles.includes(recruiter.role))
        .forEach((recruiter) => {
          const username = recruiter.username
          options.push({
            value: recruiter.id,
            label: (
              <div className="text-left">
                <div>{username} <span className="text-sm text-gray-500">{recruiter.role}</span></div>
                {/* <div className="text-sm text-gray-500">{recruiter.email}</div> */}
              </div>
            ),
            searchText: `${username} ${recruiter.email}`.toLowerCase(),
          })
        })
    } else if (!isLoadingRecruiters) {
      options.push({ value: "no-recruiters", label: "No recruiters available", disabled: true })
    }
    return options
  }, [recruiters, isLoadingRecruiters, allowedRecruiterRoles])

  const subordinateOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Subordinates" }]
    if (subordinates.length > 0) {
      subordinates.forEach((subordinate) => {
        const username = subordinate.username
        options.push({
          value: subordinate.id,
          label: (
            <div className="text-left">
              <div>{username} <span className="text-sm text-gray-500">{subordinate.role}</span></div>
              {/* <div className="text-sm text-gray-500">{subordinate.email}</div> */}
            </div>
          ),
          searchText: `${username} ${subordinate.email}`.toLowerCase(),
        })
      })
    } else if (!isLoadingSubordinates) {
      options.push({ value: "no-subordinates", label: "No subordinates available", disabled: true })
    }
    return options
  }, [subordinates, isLoadingSubordinates])

  // Handler to fetch candidate details
  const handleCandidateNameClick = async (app: Application) => {
    setIsLoadingDetails(true)
    setDetailsError(null)
    setShowCandidateDetailsModal(true)
    setSelectedApplicationForDetails(app)
    try {
      const response = await getCandidateById(app.candidate.id)
      if (!response?.data?.candidate) {
        throw new Error("No candidate data returned from API")
      }
      const candidate: Candidate = response.data.candidate
      setSelectedCandidateForDetails(candidate)
    } catch (err: any) {
      setDetailsError(err.message || "Failed to fetch candidate details")
      toast.error(err.message || "Failed to fetch candidate details", {
        duration: 4000,
      })
    } finally {
      setIsLoadingDetails(false)
    }
  }

  // Debounced search function
  const debounce = useCallback((fn: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout | null = null
    return (...args: any[]) => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        fn(...args)
        timer = null
      }, delay)
    }
  }, [])

  const debouncedApplySearch = useMemo(
    () =>
      debounce((value: string) => {
        const trimmed = value.trim()
        if (trimmed.length === 0 || trimmed.length >= 2) {
          setSearchTerm(trimmed)
          setPagination((p) => ({ ...p, page: 1 }))
        }
      }, 500),
    [debounce]
  )

  const debouncedJobSearch = useMemo(
    () => debounce((value: string) => {
      setJobSearchTerm(value)
      fetchJobs(value)
    }, 300),
    [debounce]
  )

  const debouncedRecruiterSearch = useMemo(
    () => debounce((value: string) => {
      fetchRecruiters(value)
    }, 300),
    [debounce]
  )

  const debouncedSubordinateSearch = useMemo(
    () => debounce((value: string) => {
      fetchSubordinates()
    }, 300),
    [debounce]
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearchInput(value)
      debouncedApplySearch(value)
    },
    [debouncedApplySearch]
  )

  // Add a helper to validate date range
  const isValidDateRange = useCallback(() => {
    if (!selectedDateFrom || !selectedDateTo) return true
    return new Date(selectedDateFrom) <= new Date(selectedDateTo)
  }, [selectedDateFrom, selectedDateTo])

  // Show error if invalid date range
  const [dateError, setDateError] = useState<string | null>(null)

  useEffect(() => {
    if (!isValidDateRange()) {
      setDateError("End date cannot be before start date.")
    } else {
      setDateError(null)
    }
  }, [selectedDateFrom, selectedDateTo, isValidDateRange])

  const fetchApplications = useCallback(async () => {
    const fetchId = ++fetchIdRef.current
    setIsLoading(true)
    setError(null)
    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        jobId: selectedJob !== "all" ? selectedJob : undefined,
        applicationStatus: selectedStatus !== "all" ? selectedStatus : "all",
        interviewDateFrom: isInterviewTodayStatusFilter(selectedStatus) ? undefined : (selectedDateFrom || undefined),
        interviewDateTo: isInterviewTodayStatusFilter(selectedStatus) ? undefined : (selectedDateTo || undefined),
        _t: Date.now(),
        sortBy,
        sortOrder,
      }

      if (user?.role === "HR") {
        params.recruiterId = user.id
      } else {
        if (selectedRecruiter !== "all") {
          params.recruiterId = selectedRecruiter
        }
        if (selectedSubordinate !== "all") {
          params.subordinateId = selectedSubordinate
        }
        params.userRole = user?.role
        params.userId = user?.id
      }

      const response = await getAllApplications(params)

      if (fetchId !== fetchIdRef.current) return

      const apps = (response.data.applications as unknown as Application[]) ?? []
      setApplications(apps)
      setPagination((prev) => ({
        ...prev,
        totalPages: response.totalPages,
        totalResults: response.totalResults,
      }))
    } catch (err: any) {
      if (fetchId !== fetchIdRef.current) return
      setError(err.message || "Failed to fetch applications")
      toast.error(err.message || "Failed to fetch applications", { duration: 4000 })
    } finally {
      if (fetchId === fetchIdRef.current) setIsLoading(false)
    }
  }, [
    pagination.page,
    pagination.limit,
    searchTerm,
    selectedJob,
    selectedStatus,
    selectedRecruiter,
    selectedSubordinate,
    selectedDateFrom,
    selectedDateTo,
    user?.role,
    user?.id,
    sortBy,
    sortOrder,
  ])

  useEffect(() => {
    fetchApplications()
  }, [fetchApplications])

  useEffect(() => {
    fetchJobs()
    if (user?.role !== "HR") {
      fetchRecruiters()
    }
  }, [user?.role])

  useEffect(() => {
    if (selectedRecruiter !== "all" && user?.role !== "HR") {
      fetchSubordinates()
    } else {
      setSubordinates([])
      setSelectedSubordinate("all")
    }
  }, [selectedRecruiter, user?.role])

  const fetchJobs = useCallback(async (searchTerm?: string) => {
    setIsLoadingJobs(true)
    try {
      const response = await getJobs({
        page: 1,
        limit: 10,
        search: searchTerm || undefined,
        _t: Date.now(),
      })

      if (response?.data?.jobs) {
        setJobs(response.data.jobs)
      } else {
        setJobs([])
        if (!searchTerm) {
          // toast.error("No jobs found in the system.", { duration: 3000 })
        }
      }
    } catch (err: any) {
      console.error("Failed to fetch jobs:", err)
      setJobs([])
      toast.error("Failed to load jobs for filtering")
    } finally {
      setIsLoadingJobs(false)
    }
  }, [])

  const fetchRecruiters = useCallback(async (value?: string) => {
    setIsLoadingRecruiters(true)
    try {
      let response = user.role === "ADMIN" ? await getAllUsers({role: "FBDE",search:value})  : await getMySubordinates()
      if (user.role==="ADMIN"){
        const response2 = await getAllUsers({role: "SHM",search:value})
        const response3 = await getAllUsers({role: "CONSULTANT",search:value})
        response.data.users = response.data.users.concat(response2.data.users).concat(response3.data.users)
      }
      console.log("Recruiters response:", response)
      if (response?.data?.subordinates) {
        setRecruiters(response.data.subordinates.filter((u: UserType) => allowedRecruiterRoles.includes(u.role)))
      }
      else if (response?.data?.users) {
        setRecruiters(response.data.users.filter((u: UserType) => allowedRecruiterRoles.includes(u.role)))
      } 
      else {
        setRecruiters([])
        // toast.error("No recruiters found in the system.", { duration: 3000 })
      }
    } catch (err: any) {
      console.error("Failed to fetch recruiters:", err)
      setRecruiters([])
      toast.error("Failed to load recruiters for filtering")
    } finally {
      setIsLoadingRecruiters(false)
    }
  }, [allowedRecruiterRoles])

  const fetchSubordinates = useCallback(async () => {
    setIsLoadingSubordinates(true)
    try {
      // const response = await getMySubordinates({ recruiterId: selectedRecruiter })
      const response = await getMySubordinates()

      if (response?.data?.subordinates) {
        setSubordinates(response.data.subordinates)
      } else {
        setSubordinates([])
        toast.error("No subordinates found for the selected recruiter.", { duration: 3000 })
      }
    } catch (err: any) {
      console.error("Failed to fetch subordinates:", err)
      setSubordinates([])
      toast.error("Failed to load subordinates for filtering")
    } finally {
      setIsLoadingSubordinates(false)
    }
  }, [selectedRecruiter])

  const fetchAndPopulateJobLocation = useCallback(
    async (jobId: string) => {
      try {
        const jobDetails = await getJobDetailsById(jobId)
        if (jobDetails && jobDetails.data && jobDetails.data.job) {
          const job = jobDetails.data.job
          let locationText = ""

          if (job.location) {
            locationText = job.location
          } else if (job.addressDetails) {
            const addressParts = []
            if (job.addressDetails.street) addressParts.push(job.addressDetails.street)
            if (job.addressDetails.area) addressParts.push(job.addressDetails.area)
            if (job.addressDetails.city) addressParts.push(job.addressDetails.city)
            if (job.addressDetails.pincode) addressParts.push(job.addressDetails.pincode)
            locationText = addressParts.join(", ")
          }

          if (locationText) {
            setScheduleFormData((prev) => ({
              ...prev,
              location: locationText,
            }))
          }
        }
      } catch (error) {
        console.error("Failed to fetch job location:", error)
      }
    },
    [setScheduleFormData]
  )

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page }))
    }
  }

  const handleViewCandidate = (app: Application) => {
    navigate(`/candidate/${app.candidate.id}`)
  }

  const handleEditCandidate = (app: Application) => {
    navigate(`/add-candidate/${app.candidate.id}`)
  }

  const handleDeleteCandidate = (app: Application) => {
    const candidateName = app.candidate.name || "this candidate"
    showConfirmation(
      "Delete Application",
      `Are you sure you want to delete ${candidateName}'s application?\n\nThis action cannot be undone and will remove all associated interview data.`,
      () => {
        toast.loading("Deleting application...", {
          id: "deleting-application",
          duration: 2000,
        })
        setTimeout(() => {
          toast.success(`${candidateName}'s application has been deleted successfully!`, {
            id: "deleting-application",
            duration: 3000,
          })
          fetchApplications()
        }, 2000)
      },
      {
        confirmText: "Delete",
        cancelText: "Cancel",
        type: "danger",
        onCancel: () => {
          toast("Deletion cancelled. Application data is safe.", {
            duration: 2000,
          })
        },
      }
    )
  }

  const handleUpdateStatus = async (
    applicationId: string,
    newStatus: string,
    comments?: string,
    selectedJobId?: string,
    joiningDate?: string
  ) => {
    try {
      if (newStatus === "Reassign Company" && selectedJobId) {
        setIsReassigning(true)
        await reassignCandidateApplication(applicationId, {
          newJobId: selectedJobId,
          comments: comments || `Reassigned by ${user?.username || "user"}`,
        })
        toast.success("Application reassigned successfully!")
      } else {
        await updateApplicationStatus(applicationId, {
          newStatus,
          comments: comments || `Status updated to ${newStatus} by ${user?.username || "user"}`,
          ...(newStatus === "Offered" && { offeredDate: new Date().toISOString().split("T")[0] }),
          ...(newStatus === "Joined" && { joiningDate: joiningDate }),
          ...(newStatus === "Closed - Joined Other Position" && { closedDate: new Date().toISOString().split("T")[0] }),
        })
        toast.success(`Application status updated to ${newStatus}`)
      }
      await fetchApplications()
    } catch (err: any) {
      toast.error(err.message || "Failed to update status", {
        duration: 4000,
      })
    } finally {
      setIsReassigning(false)
    }
  }

  const handleUpdateJoiningDate = async () => {
    try {
      console.log("Updating joining date with data:", statusUpdateData, joiningDate)
      if (!statusUpdateData.applicationId) return
      if (!joiningDate) {
        toast.error("Please select a valid joining date.", { duration: 3000 })
        return
      }
      await updateJoiningDate({applicationId: statusUpdateData.applicationId,comments: statusUpdateData.comments, joiningDate});
      toast.success(`Application joining date updated to ${joiningDate}`)
    } catch (error:any) {
      toast.error(error.message || "Failed to update joining date", {
        duration: 4000,
      })
    }
    finally {
      setShowJoiningDateUpdateModal(false)
    }
  }

  const handleUpdateExpectedJoiningDate = async () => {
    try {
      if (!statusUpdateData.applicationId) return
      if (!expectedJoiningDate) {
        toast.error("Please select a valid expected joining date.", { duration: 3000 })
        return
      }
      await updateExpectedJoiningDate({
        applicationId: statusUpdateData.applicationId,
        comments: statusUpdateData.comments,
        expectedJoiningDate,
      })
      toast.success(`Expected joining date updated to ${expectedJoiningDate}`)
      await fetchApplications()
    } catch (error: any) {
      toast.error(error.message || "Failed to update expected joining date", {
        duration: 4000,
      })
    } finally {
      setShowExpectedJoiningDateUpdateModal(false)
    }
  }

  const handleViewInterviewDetails = async (app: Application) => {
    setSelectedApplication(app)
    setShowInterviewModal(true)
    setShowScheduleForm(false)
    setShowLoaderInInterviewPopup(true)

    try {
      const details = await getInterviewDetailsByApplicationId(app.applicationId)
      setInterviewDetails(details)

      if (details?.data?.interview) {
        const updatedApp = {
          ...app,
          interviewId: details.data.interview.id,
          roundId:
            details.data.interview.rounds?.length > 0
              ? details.data.interview.rounds[details.data.interview.rounds.length - 1].id
              : undefined,
        }
        setSelectedApplication(updatedApp)
      }
    } catch (err: any) {
      setInterviewDetails(null)
    } finally {
      setShowLoaderInInterviewPopup(false)
    }
  }

  const handleResultRound = (round: any, result: "Qualified" | "Rejected") => {
    setCurrentRoundForAction(round)
    setResultFormData({
      result,
      reason: "",
      isFinalRound: false,
    })
    setShowResultModal(true)
  }

  const handleSubmitSchedule = async () => {
    if (!selectedApplication) return

    setIsScheduling(true)
    try {
      const missingFields = []

      if (!scheduleFormData.interviewType) missingFields.push("Interview Type")
      if (!scheduleFormData.scheduledDate) missingFields.push("Interview Date")
      if (!scheduleFormData.timeZone) missingFields.push("Time Zone")
      if (!scheduleFormData.startTime) missingFields.push("Start Time")
      if (!scheduleFormData.endTime) missingFields.push("End Time")
      if (scheduleFormData.interviewType === "Offline" && !scheduleFormData.location?.trim()) {
        missingFields.push("Interview Location")
      }

      if (missingFields.length > 0) {
        toast.error(`Please fill in all required fields:\n\n• ${missingFields.join("\n• ")}`, {
          duration: 6000,
          style: {
            maxWidth: "400px",
            whiteSpace: "pre-line",
            background: "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
            border: "1px solid #ef4444",
            borderLeft: "4px solid #dc2626",
          },
        })
        setIsScheduling(false)
        return
      }

      const validInterviewerIds = scheduleFormData.interviewers.filter(
        (id) => id !== null && id !== undefined && id !== ""
      )

      if (validInterviewerIds.length === 0 && user?.id) {
        validInterviewerIds.push(user.id)
      }

      const scheduledDateForPayload = new Date(scheduleFormData.scheduledDate)
      const scheduledDateISO = scheduledDateForPayload.toISOString().split("T")[0]

      const schedulePayload = {
        interviewType: scheduleFormData.interviewType,
        scheduledDate: scheduledDateISO,
        startTime: scheduleFormData.startTime,
        endTime: scheduleFormData.endTime,
        timeZone: scheduleFormData.timeZone,
        interviewers: validInterviewerIds,
        notes: scheduleFormData.notes || "",
        ...(scheduleFormData.interviewType === "Online" && {
          additionalParticipants: scheduleFormData.additionalParticipants || [],
        }),
        ...(scheduleFormData.interviewType === "Offline" && {
          location: scheduleFormData.location || "",
        }),
      }

      const hasValidInterviewId = selectedApplication.interviewId && selectedApplication.interviewId !== ""
      const hasValidRoundId = currentRoundForAction?.id && currentRoundForAction.id !== ""

      toast.loading("✅ Validation successful! Scheduling interview...", {
        id: "scheduling-interview",
        duration: 2000,
      })

      if (showScheduleModal) {
        await rescheduleInterviewRound(selectedApplication.interviewId || "", currentRoundForAction?.id || "", {
          ...schedulePayload,
          rescheduleReason: "Rescheduled by user",
        })
        toast.success(`Interview has been rescheduled!`, {
          id: "scheduling-interview",
          duration: 6000,
          style: {
            maxWidth: "400px",
            whiteSpace: "pre-line",
          },
        })
        try {
          setShowLoaderInInterviewPopup(true)
          await new Promise((resolve) => setTimeout(resolve, 1500))
          const updatedDetails = await getInterviewDetailsByApplicationId(selectedApplication.applicationId)
          setInterviewDetails(updatedDetails)
          setShowLoaderInInterviewPopup(false)
          setShowInterviewModal(false)
          setTimeout(() => setShowInterviewModal(true), 100)
          await fetchApplications()
        } catch (fetchErr) {
          console.log("Could not immediately fetch updated details:", fetchErr)
          setShowLoaderInInterviewPopup(false)
          await fetchApplications()
        }
      } else {
        await scheduleInterviewRound(
          selectedApplication.interviewId || "",
          currentRoundForAction?.id || "",
          schedulePayload
        )
        toast.success(`Interview scheduled successfully!`, {
          id: "scheduling-interview",
          duration: 6000,
          style: {
            maxWidth: "400px",
            whiteSpace: "pre-line",
          },
        })
      }

      setShowScheduleModal(false)
      setShowScheduleForm(false)

      try {
        setShowLoaderInInterviewPopup(true)
        await new Promise((resolve) => setTimeout(resolve, 1500))
        const updatedDetails = await getInterviewDetailsByApplicationId(selectedApplication.applicationId)
        setInterviewDetails(updatedDetails)

        if (!hasValidInterviewId && updatedDetails?.data?.interview?.id) {
          setSelectedApplication((prev) =>
            prev
              ? {
                  ...prev,
                  interviewId: updatedDetails.data.interview.id,
                }
              : null
          )
        }

        setShowLoaderInInterviewPopup(false)
        setShowInterviewModal(true)
        await fetchApplications()
      } catch (fetchErr) {
        console.log("Could not immediately fetch updated details:", fetchErr)
        setShowLoaderInInterviewPopup(false)
        await fetchApplications()
      }
    } catch (err: any) {
      console.error("Schedule error:", err)
      setShowLoaderInInterviewPopup(false)
      toast.error(err?.response?.data?.message || err?.message || "Failed to schedule interview. Please try again.", {
        duration: 5000,
      })
    } finally {
      setIsScheduling(false)
    }
  }

  const handleSubmitResult = async () => {
    if (!currentRoundForAction || !selectedApplication) return

    if (!resultFormData.reason.trim()) {
      toast.error("Please provide a detailed reason for your decision", {
        duration: 4000,
      })
      return
    }

    if (resultFormData.reason.trim().length < 10) {
      toast.error("Please provide a more detailed reason (at least 10 characters)", {
        duration: 4000,
      })
      return
    }

    if (resultFormData.isFinalRound) {
      showConfirmation(
        "Final Round Confirmation",
        `This is the final round. Are you sure you want to mark this candidate as ${resultFormData.result}?\n\nThis action will complete the interview process.`,
        () => {
          submitInterviewResult()
        },
        {
          confirmText: `Mark as ${resultFormData.result}`,
          cancelText: "Cancel",
          type: resultFormData.result === "Rejected" ? "danger" : "warning",
          onCancel: () => {
            toast("Action cancelled. No changes made.", {
              duration: 3000,
            })
          },
        }
      )
      return
    }

    submitInterviewResult()
  }

  const submitInterviewResult = async () => {
    if (!currentRoundForAction || !selectedApplication) return

    setIsSubmittingResult(true)
    try {
      const payload = {
        outcome: resultFormData.result,
        feedback: {
          rating: 5,
          recommendation: resultFormData.result,
          comments: resultFormData.reason,
          ...(resultFormData.isFinalRound &&
            resultFormData.expectedJoiningDate && { expectedJoiningDate: resultFormData.expectedJoiningDate }),
        },
        isFinalRound: resultFormData.isFinalRound,
      }

      await updateInterviewRoundStatus(selectedApplication.interviewId!, currentRoundForAction.id, payload)

      const candidateName = selectedApplication.candidate.name
      const roundName = currentRoundForAction.roundName || "Round"

      if (resultFormData.result === "Qualified") {
        if (resultFormData.isFinalRound) {
          toast.success(
            `Congratulations!\n${candidateName} has been selected!\n\n${roundName} completed\nFinal decision: Qualified\nInterview process completed successfully`,
            {
              duration: 8000,
              style: {
                maxWidth: "450px",
                whiteSpace: "pre-line",
              },
            }
          )
        } else {
          toast.success(
            `Interview result recorded\n\n${candidateName}\n${roundName} completed\nDecision: ${resultFormData.result}\nFeedback has been saved`,
            {
              duration: 6000,
              style: {
                maxWidth: "400px",
                whiteSpace: "pre-line",
              },
            }
          )
        }
      } else {
        toast.success(
          `Interview result recorded\n\n${candidateName}\n${roundName} completed\nDecision: ${resultFormData.result}\nFeedback has been saved`,
          {
            duration: 6000,
            style: {
              maxWidth: "400px",
              whiteSpace: "pre-line",
            },
          }
        )
      }

      setShowResultModal(false)

      try {
        setShowLoaderInInterviewPopup(true)
        await new Promise((resolve) => setTimeout(resolve, 1000))
        const updatedDetails = await getInterviewDetailsByApplicationId(selectedApplication.applicationId)
        setInterviewDetails(updatedDetails)
        setShowLoaderInInterviewPopup(false)

        if (resultFormData.result === "Qualified" && !resultFormData.isFinalRound) {
          toast.success("congrats you have cleared this round", {
            duration: 4000,
          })
        }

        await fetchApplications()
      } catch (fetchErr) {
        console.log("Could not immediately fetch updated details:", fetchErr)
        setShowLoaderInInterviewPopup(false)
        await fetchApplications()
      }
    } catch (err: any) {
      console.error("Result submission error:", err)
      setShowLoaderInInterviewPopup(false)
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to submit interview result. Please try again.",
        {
          duration: 5000,
        }
      )
    } finally {
      setIsSubmittingResult(false)
    }
  }

  const getApplicationsForExcel = async () => {
    try {
      let allApplications: Application[] = []
      let currentPage = 1
      let totalPages = 1

      while (currentPage <= totalPages) {
        const params: any = {
          page: currentPage,
          limit: 100,
          search: searchTerm || undefined,
          jobId: selectedJob !== "all" ? selectedJob : undefined,
          applicationStatus: selectedStatus !== "all" ? selectedStatus : undefined,
          interviewDateFrom: isInterviewTodayStatusFilter(selectedStatus) ? undefined : (selectedDateFrom || undefined),
          interviewDateTo: isInterviewTodayStatusFilter(selectedStatus) ? undefined : (selectedDateTo || undefined),
          _t: Date.now(),
        }

        if (user?.role === "HR") {
          params.recruiterId = user.id
        } else {
          if (selectedRecruiter !== "all") {
            params.recruiterId = selectedRecruiter
          }
          if (selectedSubordinate !== "all") {
            params.subordinateId = selectedSubordinate
          }
        }

        const response = await getAllApplications(params)
        const apps = (response.data.applications as unknown as Application[]) ?? []
        const applicationsWithInterviews = await Promise.all(
          apps.map(async (app) => {
            try {
              const interviewDetails = await getInterviewDetailsByApplicationId(app.applicationId)
              return {
                ...app,
                interview: interviewDetails?.data?.interview ?? undefined,
              }
            } catch (err) {
              console.error(`Failed to fetch interview for application ${app.applicationId}:`, err)
              return { ...app, interview: undefined }
            }
          })
        )
        allApplications = [...allApplications, ...applicationsWithInterviews]
        totalPages = response.totalPages
        currentPage += 1
      }

      return allApplications
    } catch (err: any) {
      console.error("Failed to fetch applications for Excel:", err)
      toast.error(err.message || "Failed to fetch applications for export", {
        duration: 4000,
      })
      return []
    }
  }

  const filteredApplications = useMemo(() => {
    if (!selectedDateFrom && !selectedDateTo) return applications

    const from = selectedDateFrom ? new Date(selectedDateFrom) : null
    const to = selectedDateTo ? new Date(selectedDateTo) : null

    return applications.filter((app) => {
      const interviewDateStr = getInterviewDate(app, interviewDetails)
      if (!interviewDateStr) return false

      const interviewDate = new Date(interviewDateStr)

      if (from && interviewDate < from) return false
      if (to && interviewDate > to) return false
      return true
    })
  }, [applications, selectedDateFrom, selectedDateTo, interviewDetails])

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortBy(column)
      setSortOrder("asc")
    }
  }

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className,
  }) => (
    <th
      className={cn(
        "px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10",
        className
      )}
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  )

  const handleChangeCandidateStatus = async (app: Application) => {
    setShowCandidateDetailsModal(false)
    const currentStatus = app.currentStatus?.status || ""
    setStatusUpdateData({
      applicationId: app.applicationId,
      newStatus: getDefaultStatusChangeValue(currentStatus),
      comments: "",
      currentStatus,
    })
    setShowStatusUpdateModal(true)
  }

  const handleInterviewStatusChange = (app: Application, newStatus: string) => {
    const candidateName = app.candidate.name || "this candidate"
    showConfirmation(
      `Confirm ${newStatus === "Interview Attended" ? "Attendance" : "Non-Attendance"}`,
      `Are you sure you want to mark ${candidateName}'s interview as "${newStatus}"?`,
      async () => {
        try {
          await updateApplicationStatus(app.applicationId, {
            newStatus,
            comments: `Interview status updated to ${newStatus} by ${user?.username || "user"}`,
          })
          toast.success(`Interview status updated to ${newStatus}`)
          await fetchApplications()
        } catch (err: any) {
          toast.error(err.message || "Failed to update interview status", {
            duration: 4000,
          })
        }
      },
    )
  }

  const today = new Date()
  const minDate = new Date()
  minDate.setDate(today.getDate() - 20)
 
  const renderActionsDropdown = (app: Application) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          <MoreHorizontal className="h-4 w-4" />

        </Button>

        

      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation()
            handleChangeCandidateStatus(app)
          }}
          disabled={!CHANGE_STATUS_ENABLED_STATUSES.includes(app.currentStatus?.status || "")}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Change Status
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation()
            setStatusUpdateData({
              applicationId: app.applicationId,
              newStatus: "Reassign Company",
              comments: "",
              selectedJobId: "",
            })
            setShowStatusUpdateModal(true)
          }}
          disabled={app.currentStatus?.status === "Joined"}
        >
          <UserCheck className="mr-2 h-4 w-4" />
          Reassign Company
        </DropdownMenuItem>
        {user?.role === "ADMIN" && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              setTimeout(() => {
                setChangeCompanyData({
                  applicationId: app.applicationId,
                  selectedJobId: "",
                  selectedJobLabel: "",
                  comments: "",
                  candidateName: app.candidate?.name || "",
                  currentCompany: app.company?.companyName || "",
                })
                setShowChangeCompanyModal(true)
              }, 0)
            }}
            disabled={CHANGE_COMPANY_BLOCKED_STATUSES.includes(app.currentStatus?.status || "")}
          >
            <Building className="mr-2 h-4 w-4" />
            Change Company
          </DropdownMenuItem>
        )}

        {(["Joined","Tenure Pending","Tenure Approved"].includes(app.currentStatus?.status) && user?.role === "ADMIN") && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              setStatusUpdateData((prev) => ({ ...prev, applicationId:app.applicationId }))
              setShowJoiningDateUpdateModal(true);
            }}
          >
            <UserCheck className="mr-2 h-4 w-4" />
            Change Joining Date
          </DropdownMenuItem>
        )}
        {(app.currentStatus?.status === "Interview Scheduled" || app.currentStatus?.status === "Interview Rescheduled") && (
          <>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                handleInterviewStatusChange(app, "Interview Attended")
              }}
            >
              <UserCheck className="mr-2 h-4 w-4" />
              Interview Attended
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                handleInterviewStatusChange(app, "Interview Not Attended")
              }}
            >
              <UserCheck className="mr-2 h-4 w-4" />
              Interview Not Attended
            </DropdownMenuItem>
          </>
        )}
        {(user?.role === "ADMIN" || user?.role === "SHM") && app.candidate?.id && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditCandidateApp(app)
                    setEditCandidateForm({
                      name: app.candidate.name || "",
                      email: app.candidate.email || "",
                      phone: app.candidate.phone || "",
                    })
                    setEditCandidateFieldErrors({ name: "", email: "", phone: "" })
                    setShowEditCandidateModal(true)
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit candidate
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px] text-xs">
                Admin / SHM only. Updates name, email, and phone on the candidate profile. Each save is logged with
                previous and new values.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {app.company.requireAdditionalDetails && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              navigator.clipboard.writeText(`${process.env.REACT_APP_NEXT_FRONT_END_URL}/update-candidate-details/${app.candidate.id}`)
              toast.success("Profile update link copied to clipboard")
            }}
          >
            <Link className="mr-2 h-4 w-4" />
            copy profile update link
          </DropdownMenuItem>
        )}

        {/* Expected Joining Date (different from joining date) */}
        {["ADMIN", "HR", "HM", "AGENCY", "SHM", "FBDE", "Admin",].includes(user?.role || "") &&
          app.currentStatus?.status === "Selected" && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              setStatusUpdateData((prev) => ({ ...prev, applicationId: app.applicationId, comments: "" }))
              setExpectedJoiningDate(
                app.expectedJoiningDate
                  ? new Date(app.expectedJoiningDate).toISOString().split("T")[0]
                  : new Date().toISOString().split("T")[0]
              )
              setShowExpectedJoiningDateUpdateModal(true)
            }}
          >
            <UserCheck className="mr-2 h-4 w-4" />
            Expected Joining Date
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const handleExcelDownload = async () => {
    setIsExportingExcel(true)
    try {
      toast.loading("Preparing export...", { id: "applications-excel-loader" })

      const EXPORT_BATCH_SIZE = 5000
      const MAX_EXPORT_ROWS = 200000
      const baseParams: Record<string, string | undefined> = {
        search: searchTerm || undefined,
        jobId: selectedJob !== "all" ? selectedJob : undefined,
        applicationStatus: selectedStatus !== "all" ? selectedStatus : undefined,
        recruiterId: user?.role === "HR" ? user.id : selectedRecruiter !== "all" ? selectedRecruiter : undefined,
        subordinateId: selectedSubordinate !== "all" ? selectedSubordinate : undefined,
        interviewDateFrom: isInterviewTodayStatusFilter(selectedStatus) ? undefined : (selectedDateFrom || undefined),
        interviewDateTo: isInterviewTodayStatusFilter(selectedStatus) ? undefined : (selectedDateTo || undefined),
      }

      let allApplications: Record<string, unknown>[] = []
      let cursor: string | null = null
      let hasNextPage = true
      let totalResults = 0

      while (hasNextPage && allApplications.length < MAX_EXPORT_ROWS) {
        const remaining = MAX_EXPORT_ROWS - allApplications.length
        const cursorParam = cursor
        const response: ExportApplicationsResponse = await fetchApplicationsExportBatch({
          ...baseParams,
          ...(cursorParam ? { cursor: cursorParam } : {}),
          limit: Math.min(EXPORT_BATCH_SIZE, remaining),
        })

        const batch = response?.applications || []
        totalResults = response?.totalResults ?? totalResults
        allApplications = allApplications.concat(batch)
        hasNextPage = Boolean(response?.hasNextPage) && allApplications.length < MAX_EXPORT_ROWS
        cursor = response?.nextCursor || null

        const progressLabel =
          totalResults > 0
            ? `Exporting ${allApplications.length.toLocaleString()} of ${totalResults.toLocaleString()} rows...`
            : `Exporting ${allApplications.length.toLocaleString()} rows...`
        toast.loading(progressLabel, { id: "applications-excel-loader" })

        if (batch.length === 0) {
          break
        }
      }

      if (allApplications.length === 0) {
        toast.error("No applications found to export.", { id: "applications-excel-loader" })
        return
      }

      const worksheet = XLSX.utils.json_to_sheet(allApplications)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "Applications")

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `applications_${format(new Date(), "yyyy-MM-dd")}.xlsx`)
      document.body.appendChild(link)
      link.click()

      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)

      const truncated = hasNextPage || (totalResults > 0 && allApplications.length < totalResults)
      if (truncated) {
        toast.success(
          `Exported ${allApplications.length.toLocaleString()} of ${totalResults.toLocaleString()} rows. Narrow filters to export a smaller set.`,
          { id: "applications-excel-loader", duration: 6000 }
        )
      } else {
        toast.success("Excel file downloaded successfully!", { id: "applications-excel-loader" })
      }
    } catch (error: any) {
      console.error("Failed to export applications:", error)
      toast.error(
        `Failed to export: ${error?.response?.data?.message || error?.message || "Unknown error"}`,
        { id: "applications-excel-loader" }
      )
    } finally {
      setIsExportingExcel(false)
    }
  }

  return (
    <div data-page-scroll="internal" className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-2 sm:px-6 py-3 sm:py-4">
        <div className="w-full">
          <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Applications</h1>
                <p className="text-xs sm:text-sm text-gray-500">Manage and track candidate applications efficiently</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search Applications..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  className="pl-10 w-full sm:w-64 border-gray-300"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleExcelDownload}
                disabled={isExportingExcel}
                className="flex items-center justify-center gap-2 rounded-lg border-orange-300 text-orange-600 hover:bg-orange-50 w-full sm:w-auto"
              >
                {isExportingExcel ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="hidden sm:inline">Downloading...</span>
                    <span className="sm:hidden">Loading...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="hidden sm:inline">Download Excel</span>
                    <span className="sm:hidden">Excel</span>
                  </>
                )}
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2 sm:mt-3 w-full">
            <div className="min-w-0 w-full flex-[1_1_240px]">
            <SearchableSelect
              options={jobs
                .filter((job) => job.status === "published")
                .map((job) => ({
                  value: job.id,
                  label: `${job.title} — ${job?.companyName || "N/A"} · ${job?.location || "N/A"}`,
                  searchText: `${job.title} ${job?.companyName} ${job?.location}`,
                }))}
              value={selectedJob}
              onValueChange={(value) => {
                setSelectedJob(value)
                setPagination((p) => ({ ...p, page: 1 }))
              }}
              onSearchChange={debouncedJobSearch}
              placeholder={isLoadingJobs ? "Loading jobs..." : "All Jobs"}
              searchPlaceholder="Search jobs..."
              icon={<Building className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />}
              emptyText="No jobs available"
              className="w-full rounded-lg border-gray-200 text-black focus:ring-2 focus:ring-orange-500 h-9 sm:h-10"
              loading={isLoadingJobs}
            />
            </div>
            <div className="min-w-0 w-full sm:w-auto flex-[0_1_160px]">
            <SearchableSelect
              options={APPLICATION_STATUS_FILTER_OPTIONS}
              value={selectedStatus}
              onValueChange={(v) => {
                setSelectedStatus(v)
                if (isInterviewTodayStatusFilter(v)) {
                  setSelectedDateFrom("")
                  setSelectedDateTo("")
                }
                setPagination((p) => ({ ...p, page: 1 }))
              }}
              placeholder="All Status"
              searchPlaceholder="Search status..."
              icon={<CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />}
              emptyText="No status available"
              className="w-full rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 h-9 sm:h-10"
            />
            </div>
            {!['HR', 'INVESTOR'].includes(user?.role || "") && (
              <div className="min-w-0 w-full flex-[1_1_180px]">
                <SearchableSelect
                  options={recruiterOptions}
                  value={selectedRecruiter}
                  onValueChange={(v) => {
                    setSelectedRecruiter(v)
                    setSelectedSubordinate("all")
                    setPagination((p) => ({ ...p, page: 1 }))
                  }}
                  onSearchChange={debouncedRecruiterSearch}
                  placeholder={isLoadingRecruiters ? "Loading recruiters..." : "All Recruiters"}
                  searchPlaceholder="Search recruiters..."
                  icon={<User className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />}
                  emptyText="No recruiters available"
                  className="w-full rounded-lg border-gray-200 text-black focus:ring-2 focus:ring-orange-500 h-9 sm:h-10"
                  loading={isLoadingRecruiters}
                />
              </div>
            )}
            {!isInterviewTodayStatusFilter(selectedStatus) && (
            <div className="flex gap-2 min-w-0 w-full sm:w-auto shrink-0">
              <Input
                type="date"
                value={selectedDateFrom}
                onChange={(e) => {
                  setSelectedDateFrom(e.target.value)
                  setPagination((p) => ({ ...p, page: 1 }))
                }}
                max={selectedDateTo || undefined}
                className="w-full sm:w-[148px] h-9 sm:h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 shrink-0"
                title="From Date"
                placeholder="From Date"
              />
              <Input
                type="date"
                value={selectedDateTo}
                onChange={(e) => {
                  setSelectedDateTo(e.target.value)
                  setPagination((p) => ({ ...p, page: 1 }))
                }}
                min={selectedDateFrom || undefined}
                className="w-full sm:w-[148px] h-9 sm:h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 shrink-0"
                title="To Date"
                placeholder="To Date"
              />
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Candidate Details Modal */}
      <Dialog open={showCandidateDetailsModal} onOpenChange={setShowCandidateDetailsModal}>
        <DialogContent className="w-[95vw] max-w-xs sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl sm:rounded-3xl no-scrollbar">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="h-10 w-10 sm:h-12 sm:w-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                {selectedCandidateForDetails?.name.charAt(0).toUpperCase()}
              </div>
              <span className="break-words">{selectedCandidateForDetails?.name}</span>
            </DialogTitle>
          </DialogHeader>
          {isLoadingDetails ? (
            <div className="text-center py-8 sm:py-12">
              <div className="animate-spin inline-block h-8 w-8 sm:h-10 sm:w-10 border-4 border-orange-600 border-t-transparent rounded-full mb-4"></div>
              <p className="text-base sm:text-lg text-gray-600">Loading candidate details...</p>
            </div>
          ) : detailsError ? (
            <div className="text-center py-8 sm:py-12">
              <p className="text-base sm:text-lg text-red-600">{detailsError}</p>
            </div>
          ) : selectedCandidateForDetails ? (
            <div className="space-y-4 sm:space-y-6 p-0">
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <User className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Mail className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Email</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base break-words">
                          {selectedCandidateForDetails.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Phone className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Phone</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.phone}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <User className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Father's Name</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.fatherName || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <UserCheck className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Gender</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.gender || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Location</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.currentLocationDetails?.city || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Baby className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Date of Birth</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.dateOfBirth
                            ? new Date(selectedCandidateForDetails.dateOfBirth).toLocaleDateString("en-GB")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CreditCard className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Aadhar Number</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.aadharNumber || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <GraduationCap className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Highest Qualification</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.highestQualification || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Languages className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Spoken Languages</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.spokenLanguages &&
                          selectedCandidateForDetails.spokenLanguages.length > 0
                            ? selectedCandidateForDetails.spokenLanguages.join(", ")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Briefcase className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Experience</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.totalExperienceYears !== undefined &&
                          selectedCandidateForDetails.totalExperienceMonths !== undefined
                            ? `${selectedCandidateForDetails.totalExperienceYears} years ${selectedCandidateForDetails.totalExperienceMonths} months`
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Award className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Skills</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Array.isArray(selectedCandidateForDetails.skills) &&
                          selectedCandidateForDetails.skills.length > 0 ? (
                            selectedCandidateForDetails.skills.slice(0, 3).map((skill, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))
                          ) : (
                            <span className="font-medium text-gray-900 text-sm sm:text-base">N/A</span>
                          )}
                          {Array.isArray(selectedCandidateForDetails.skills) &&
                            selectedCandidateForDetails.skills.length > 3 && (
                              <Badge variant="secondary" className="text-xs">
                                +{selectedCandidateForDetails.skills.length - 3} more
                              </Badge>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                  Professional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <Building className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Job Category</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {Array.isArray(selectedCandidateForDetails.preferredJobCategories) &&
                          selectedCandidateForDetails.preferredJobCategories.length > 0
                            ? selectedCandidateForDetails.preferredJobCategories.join(", ")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Employment Type</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {Array.isArray(selectedCandidateForDetails.preferredEmploymentTypes) &&
                          selectedCandidateForDetails.preferredEmploymentTypes.length > 0
                            ? selectedCandidateForDetails.preferredEmploymentTypes.join(", ")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

       
{selectedCandidateForDetails.additionalDetails && <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
    <Info className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
    Additional Information
  </h3>

  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

    {/* Alternate Mobile */}
    <div className="flex items-start gap-3">
      <Smartphone className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm text-gray-600">Alternate Mobile Number</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base">
          {selectedCandidateForDetails.additionalDetails?.alternateMobileNumber || "N/A"}
        </p>
      </div>
    </div>

 

    <div className="flex items-start gap-3">
      <IdCard className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm text-gray-600">PAN Number</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base">
          {selectedCandidateForDetails.additionalDetails?.panNumber || "N/A"}
        </p>
      </div>
    </div>

    <div className="flex items-start gap-3">
      <History className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm text-gray-600">Previous Experience</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base">
          {selectedCandidateForDetails.additionalDetails?.previousExperience || "N/A"}
        </p>
      </div>
    </div>

    <div className="flex items-start gap-3">
      <GraduationCap className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm text-gray-600">Qualification</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base">
          {selectedCandidateForDetails.additionalDetails?.qualification || "N/A"}
        </p>
      </div>
    </div>

    {/* Permanent Address */}
    <div className="flex items-start gap-3">
      <Globe className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm text-gray-600">Permanent Address</p>
      
        <p className="font-medium text-gray-900 text-sm sm:text-base">{selectedCandidateForDetails.additionalDetails?.permanentAddress?.street || "N/A"}</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base"> {selectedCandidateForDetails.additionalDetails?.permanentAddress?.area || "N/A"}</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base"> {selectedCandidateForDetails.additionalDetails?.permanentAddress?.city || "N/A"}</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base"> {selectedCandidateForDetails.additionalDetails?.permanentAddress?.state || "N/A"}</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base"> {selectedCandidateForDetails.additionalDetails?.permanentAddress?.pincode || "N/A"}</p>
       
      </div>
    </div>

    {/* Current Address */}
    <div className="flex items-start gap-3">
      <MapPin className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm text-gray-600">Current Address</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base">
          {selectedCandidateForDetails.additionalDetails?.currentAddress?.street || "N/A"} </p>
        <p className="font-medium text-gray-900 text-sm sm:text-base"> {selectedCandidateForDetails.additionalDetails?.currentAddress?.area || "N/A"}</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base"> {selectedCandidateForDetails.additionalDetails?.currentAddress?.city || "N/A"}</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base"> {selectedCandidateForDetails.additionalDetails?.currentAddress?.state || "N/A"}</p>
        <p className="font-medium text-gray-900 text-sm sm:text-base"> {selectedCandidateForDetails.additionalDetails?.currentAddress?.pincode || "N/A"}</p>
       
      </div>
    </div>

  </div>
</div>}

              <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                  Application Status
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2">Current Status</p>
                    <Badge
                      className={`${getStatusBadgeColor(selectedApplicationForDetails?.currentStatus?.status ?? "")} border font-medium px-3 py-1 text-xs sm:text-sm`}
                    >
                      {selectedApplicationForDetails?.currentStatus?.status ?? "N/A"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2">Expected Joining Date</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900 text-sm sm:text-base">
                        {(() => {
                          const expected = selectedApplicationForDetails?.expectedJoiningDate || ""
                          if (!expected) return "N/A"
                          const d = new Date(expected)
                          return isNaN(d.getTime()) ? expected : d.toLocaleDateString("en-GB")
                        })()}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2">Sourced By</p>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails?.sourcedBy?.username ?? "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
          
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Status Update Modal */}
      <Dialog
        open={showStatusUpdateModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowStatusUpdateModal(false)
            setStatusUpdateData({ applicationId: "", newStatus: "", comments: "", selectedJobId: "", currentStatus: "" })
            setReassignMissingFields([])
            setCandidateIdForReassign("")
            setReassignFormValues({})
          }
        }}
      >
        <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl flex flex-col">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900">Update Application Status</DialogTitle>
            <DialogDescription className="text-sm sm:text-base text-gray-600">
              Select a new status for the application and provide any additional comments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-y-auto pr-1">
            <div className="flex flex-col sm:flex-row sm:justify-between w-full gap-4 sm:gap-2">
              <div className={`flex flex-col w-full ${isNotSelectedOnlyStatusChange(statusUpdateData.currentStatus || "") ? "" : "sm:w-[47%]"}`}>
                <label className="text-sm font-medium text-gray-700">New Status</label>
                <SearchableSelect
                  options={getStatusChangeOptions(statusUpdateData.currentStatus || "")}
                  value={statusUpdateData.newStatus}
                  onValueChange={(value) => setStatusUpdateData((prev) => ({ ...prev, newStatus: value }))}
                  placeholder="Select status"
                  searchPlaceholder="Search status..."
                  emptyText="No status available"
                  className="w-full mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-400 h-10"
                />
              </div>
              {statusUpdateData.currentStatus && !isNotSelectedOnlyStatusChange(statusUpdateData.currentStatus) && (
              <div className="flex flex-col w-full sm:w-[47%]">
                <label
                  className={`text-sm font-medium ${statusUpdateData.newStatus !== "Joined"
                      ? "text-gray-400"
                      : "text-gray-700"
                    }`}
                >
                  {statusUpdateData.newStatus === "Joined"
                    ? "Select Joining Date"
                    : statusUpdateData.newStatus === "Offered"
                      ? "Select Offered Date"
                      : "Select Reassigned Date"}
                </label>
                <Input
                  type="date"
                  value={joiningDate}
                  onChange={(e) => setJoiningDate(e.target.value)}
                  disabled={!showDateSelect}
                  min={minDate.toISOString().split("T")[0]}
                  max={new Date().toISOString().split("T")[0]}
                  className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 mt-1"
                  title="Select Joining Date"
                />
              </div>
              )}
            </div>
            {statusUpdateData.newStatus === "Reassign Company" && (
              <div>
                <label className="text-sm font-medium text-gray-700">Select New Job</label>
                <SearchableSelect
                  options={jobs
                    .filter((job) => job.status === "published")
                    .map((job) => ({
                      value: job.id,
                      label: (
                        <div className="text-left">
                          <div>{job.title}</div>
                          <div className="text-sm text-gray-500">{job?.companyName}</div>
                          <div className="text-sm text-gray-500">{job?.location}</div>
                        </div>
                      ),
                      searchText: `${job.title} ${job?.companyName} ${job?.location}`,
                    }))}
                  value={statusUpdateData.selectedJobId || ""}
                  onValueChange={(value) => {
                    setStatusUpdateData((prev) => ({ ...prev, selectedJobId: value }))
                    setReassignMissingFields([])
                    setReassignFormValues({})
                  }}
                  onSearchChange={debouncedJobSearch}
                  placeholder="Search and select a job..."
                  searchPlaceholder="Search jobs..."
                  emptyText="No jobs available"
                  className="w-full mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 h-10"
                  loading={isLoadingJobs}
                />
              </div>
            )}
            {statusUpdateData.newStatus === "Reassign Company" && reassignMissingFields.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                <p className="text-sm font-medium text-amber-800">This company requires the following details. Please fill them below:</p>
                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {reassignMissingFields.map((key) => (
                    <div key={key} className="space-y-1">
                      <label className="text-sm font-medium text-gray-700">{REASSIGN_FIELD_LABELS[key]}</label>
                      {key === "maritalStatus" ? (
                        <Select
                          value={reassignFormValues[key] || ""}
                          onValueChange={(v) => {
                            setReassignFormValues((prev) => ({ ...prev, [key]: v }))
                            setReassignFieldErrors((prev) => {
                              const next = { ...prev }
                              delete next[key]
                              return next
                            })
                          }}
                        >
                          <SelectTrigger className="h-9 bg-white border-gray-200">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Single">Single</SelectItem>
                            <SelectItem value="Married">Married</SelectItem>
                            <SelectItem value="Divorced">Divorced</SelectItem>
                            <SelectItem value="Widowed">Widowed</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : key === "howOftenAbleToJoin" ? (
                        <Select
                          value={reassignFormValues[key] || ""}
                          onValueChange={(v) => {
                            setReassignFormValues((prev) => ({ ...prev, [key]: v }))
                            setReassignFieldErrors((prev) => {
                              const next = { ...prev }
                              if (!v) {
                                next[key] = `${REASSIGN_FIELD_LABELS[key]} is required`
                              } else {
                                delete next[key]
                              }
                              return next
                            })
                          }}
                        >
                          <SelectTrigger className="h-9 bg-white border-gray-200">
                            <SelectValue placeholder="Select availability" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Immediately">Immediately</SelectItem>
                            <SelectItem value="Within 1 week">Within 1 week</SelectItem>
                            <SelectItem value="Within 2 weeks">Within 2 weeks</SelectItem>
                            <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                            <SelectItem value="Within 2 months">Within 2 months</SelectItem>
                            <SelectItem value="More than 2 months">More than 2 months</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : key === "resumeUrl" ? (
                        <div className="flex gap-2">
                          <Input
                            value={reassignFormValues[key] || ""}
                            onChange={(e) => {
                              const value = e.target.value
                              setReassignFormValues((prev) => ({ ...prev, [key]: value }))
                              setReassignFieldErrors((prev) => {
                                const next = { ...prev }
                                if (!value.trim()) {
                                  next[key] = "Resume URL is required"
                                } else {
                                  delete next[key]
                                }
                                return next
                              })
                            }}
                            placeholder="Paste URL or upload file"
                            className="flex-1 h-9 bg-white border-gray-200"
                          />
                          <label className="flex items-center justify-center px-3 h-9 rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50">
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0]
                                if (!file) return
                                try {
                                  const res = await uploadFile(file, "resumes")
                                  const url = (res as any)?.fileUrl ?? (res as any)?.url ?? ""
                                  if (url) {
                                    setReassignFormValues((prev) => ({ ...prev, [key]: url }))
                                    setReassignFieldErrors((prev) => {
                                      const next = { ...prev }
                                      delete next[key]
                                      return next
                                    })
                                  }
                                } catch (err) {
                                  toast.error("Resume upload failed")
                                }
                                e.target.value = ""
                              }}
                            />
                            Upload
                          </label>
                        </div>
                      ) : (
                        <Input
                          value={reassignFormValues[key] || ""}
                          onChange={(e) => {
                            const value = e.target.value
                            setReassignFormValues((prev) => ({ ...prev, [key]: value }))
                            setReassignFieldErrors((prev) => {
                              const next = { ...prev }
                              const v = value.trim()
                              if (!v) {
                                next[key] = `${REASSIGN_FIELD_LABELS[key]} is required`
                              } else if (key === "alternativePhoneNumber" && !validatePhone10(v)) {
                                next[key] = "Enter a valid 10-digit phone number"
                              } else if (key === "aadharNumber" && !validateAadhar12(v)) {
                                next[key] = "Enter a valid 12-digit Aadhar number"
                              } else if (key === "pincode" && !validatePincode6(v)) {
                                next[key] = "Enter a valid 6-digit pincode"
                              } else {
                                delete next[key]
                              }
                              return next
                            })
                          }}
                          placeholder={REASSIGN_FIELD_LABELS[key]}
                          className="h-9 bg-white border-gray-200"
                          type={key === "alternativePhoneNumber" || key === "aadharNumber" || key === "pincode" ? "tel" : "text"}
                          maxLength={key === "aadharNumber" ? 12 : key === "pincode" ? 6 : undefined}
                        />
                      )}
                      {reassignFieldErrors[key] && (
                        <p className="text-xs text-red-500">{reassignFieldErrors[key]}</p>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                  disabled={
                    reassignSaving ||
                    reassignMissingFields.some((k) => !reassignFormValues[k]?.trim()) ||
                    Object.keys(reassignFieldErrors).length > 0
                  }
                  onClick={async () => {
                    if (!candidateIdForReassign) return
                    const empty = reassignMissingFields.filter((k) => !reassignFormValues[k]?.trim())
                    if (empty.length > 0) {
                      const errs: Record<string, string> = {}
                      empty.forEach((k) => {
                        errs[k] = `${REASSIGN_FIELD_LABELS[k]} is required`
                      })
                      setReassignFieldErrors(errs)
                      toast.error("Please fill all required fields")
                      return
                    }
                    // Final validation pass for formats
                    const formatErrors: Record<string, string> = {}
                    reassignMissingFields.forEach((k) => {
                      const v = reassignFormValues[k]?.trim() || ""
                      if (k === "alternativePhoneNumber" && !validatePhone10(v)) {
                        formatErrors[k] = "Enter a valid 10-digit phone number"
                      } else if (k === "aadharNumber" && !validateAadhar12(v)) {
                        formatErrors[k] = "Enter a valid 12-digit Aadhar number"
                      } else if (k === "pincode" && !validatePincode6(v)) {
                        formatErrors[k] = "Enter a valid 6-digit pincode"
                      } else if (k === "resumeUrl" && !v) {
                        formatErrors[k] = "Resume URL is required"
                      }
                    })
                    if (Object.keys(formatErrors).length > 0) {
                      setReassignFieldErrors(formatErrors)
                      toast.error("Please correct the highlighted fields")
                      return
                    }
                    setReassignSaving(true)
                    try {
                      const payload: Record<string, string> = {}
                      reassignMissingFields.forEach((k) => {
                        const v = reassignFormValues[k]?.trim()
                        if (v) payload[k] = v
                      })
                      await updateCandidateAdditionalDetailsForReassign(candidateIdForReassign, payload)
                      setReassignMissingFields([])
                      setReassignFormValues({})
                      setCandidateIdForReassign("")
                      await handleUpdateStatus(
                        statusUpdateData.applicationId,
                        statusUpdateData.newStatus,
                        statusUpdateData.comments,
                        statusUpdateData.selectedJobId,
                        joiningDate
                      )
                      setShowStatusUpdateModal(false)
                      setStatusUpdateData({ applicationId: "", newStatus: "", comments: "", selectedJobId: "", currentStatus: "" })
                      toast.success("Details updated and application reassigned successfully!")
                    } catch (err: any) {
                      toast.error(err?.response?.data?.message || err?.message || "Failed to update or reassign")
                    } finally {
                      setReassignSaving(false)
                    }
                  }}
                >
                  {reassignSaving ? "Saving..." : "Save & Reassign"}
                </Button>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700">Comments</label>
              <Textarea
                value={statusUpdateData.comments}
                onChange={(e) => setStatusUpdateData((prev) => ({ ...prev, comments: e.target.value }))}
                placeholder="Enter comments (optional)"
                className="w-full h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-400"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowStatusUpdateModal(false)
                setStatusUpdateData({ applicationId: "", newStatus: "", comments: "", selectedJobId: "", currentStatus: "" })
              }}
              className="rounded-xl border-gray-200 w-full sm:w-auto order-2 sm:order-1"
              disabled={isReassigning}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (statusUpdateData.newStatus === "Reassign Company" && !statusUpdateData.selectedJobId) {
                  toast.error("Please select a job to reassign to")
                  return
                }
                if (statusUpdateData.newStatus === "Reassign Company" && statusUpdateData.selectedJobId && isJobFromSpecialCompany(jobs, statusUpdateData.selectedJobId)) {
                  const app = applications.find((a) => a.applicationId === statusUpdateData.applicationId)
                  if (app?.candidate?.id) {
                    try {
                      const res = await getCandidateById(app.candidate.id)
                      const candidate = res?.data?.candidate
                      const missing = getMissingMandatoryFieldsForReassign(candidate)
                      if (missing.length > 0) {
                        setReassignMissingFields(missing)
                        setCandidateIdForReassign(app.candidate.id)
                        const addr = candidate?.additionalDetails?.currentAddress ?? candidate?.currentLocationDetails
                        const initial: Record<string, string> = {}
                        missing.forEach((key) => {
                          if (key === "alternativePhoneNumber") initial[key] = candidate?.additionalDetails?.alternateMobileNumber ?? candidate?.alternativePhoneNumber ?? ""
                          else if (key === "maritalStatus") initial[key] = candidate?.maritalStatus ?? ""
                          else if (key === "howOftenAbleToJoin") initial[key] = candidate?.howOftenAbleToJoin ?? ""
                          else if (key === "aadharNumber") initial[key] = candidate?.aadharNumber ?? ""
                          else if (key === "resumeUrl") initial[key] = candidate?.resumeUrl ?? candidate?.resume ?? ""
                          else if (key === "street") initial[key] = addr?.street ?? ""
                          else if (key === "area") initial[key] = addr?.area ?? ""
                          else if (key === "pincode") initial[key] = addr?.pincode ?? ""
                        })
                        setReassignFormValues(initial)
                        toast.error("This company requires additional details. Please fill the fields below and click Save & Reassign.")
                        return
                      }
                    } catch (e) {
                      toast.error("Could not verify candidate details. Please try again.")
                      return
                    }
                  }
                }
                setReassignMissingFields([])
                setCandidateIdForReassign("")
                await handleUpdateStatus(
                  statusUpdateData.applicationId,
                  statusUpdateData.newStatus,
                  statusUpdateData.comments,
                  statusUpdateData.selectedJobId,
                  joiningDate
                )
                setShowStatusUpdateModal(false)
                setStatusUpdateData({ applicationId: "", newStatus: "", comments: "", selectedJobId: "", currentStatus: "" })
              }}
              disabled={!statusUpdateData.newStatus || isReassigning || reassignMissingFields.length > 0}
              className="rounded-xl bg-orange-600 text-white hover:bg-orange-700 w-full sm:w-auto order-1 sm:order-2"
            >
              {isReassigning ? "Processing..." : reassignMissingFields.length > 0 ? "Fill details below" : "Update Status"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* change joining Date Modal */}
      <Dialog
        open={showJoiningDateUpdateModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowJoiningDateUpdateModal(false)
          }
        }}
      >
        <DialogContent className="w-[95vw] max-w-[500px] rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900">Update Joining Date</DialogTitle>
            <DialogDescription className="text-sm sm:text-base text-gray-600">
              Update the joining for the application and provide any additional comments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between w-full gap-4 sm:gap-2">
              <div className="flex flex-col w-full sm:w-[47%]">
                <label className="text-sm font-medium text-gray-700">New Status</label>
                <SearchableSelect
                  options={[
                    { value: "Joined", label: "Joined" },
                    { value: "Offered", label: "Offered" },
                    { value: "Reassign Company", label: "Reassign Company" },
                  ]}
                  disabled
                  value={statusUpdateData.newStatus}
                  onValueChange={(value) => setStatusUpdateData((prev) => ({ ...prev, newStatus: value }))}
                  placeholder="Select status"
                  searchPlaceholder="Search status..."
                  emptyText="No status available"
                  className="w-full mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-400 h-10"
                />
              </div>
              <div className="flex flex-col w-full sm:w-[47%]">
                <label
                  className="text-sm font-medium text-gray-700"
                >
                 Select Joining Date
                </label>
                <Input
                  type="date"
                  value={joiningDate}
                  onChange={(e) => setJoiningDate(e.target.value)}
                  max={new Date().toLocaleDateString("en-CA")}
                  className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 mt-1"
                  title="Select Joining Date"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Comments</label>
              <Textarea
                value={statusUpdateData.comments}
                onChange={(e) => setStatusUpdateData((prev) => ({ ...prev, comments: e.target.value }))}
                placeholder="Enter comments (optional)"
                className="w-full h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-400"
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowJoiningDateUpdateModal(false)
                }}
                className="rounded-xl border-gray-200 w-full sm:w-auto order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await handleUpdateJoiningDate()
                  setShowJoiningDateUpdateModal(false)
                  setStatusUpdateData({ applicationId: "", newStatus: "", comments: "", selectedJobId: "", currentStatus: "" })
                }}
                disabled={!joiningDate}
                className="rounded-xl bg-orange-600 text-white hover:bg-orange-700 w-full sm:w-auto order-1 sm:order-2"
              >
               Update Status
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expected Joining Date Modal (different from joining date) */}
      <Dialog
        open={showExpectedJoiningDateUpdateModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowExpectedJoiningDateUpdateModal(false)
          }
        }}
      >
        <DialogContent className="w-[95vw] max-w-[500px] rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900">Update Expected Joining Date</DialogTitle>
            <DialogDescription className="text-sm sm:text-base text-gray-600">
              Update the expected joining date for the application and provide any additional comments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col w-full">
              <label className="text-sm font-medium text-gray-700">Select Expected Joining Date</label>
              <Input
                type="date"
                value={expectedJoiningDate}
                onChange={(e) => setExpectedJoiningDate(e.target.value)}
                min={new Date().toLocaleDateString("en-CA")}
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 mt-1"
                title="Select Expected Joining Date"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Comments</label>
              <Textarea
                value={statusUpdateData.comments}
                onChange={(e) => setStatusUpdateData((prev) => ({ ...prev, comments: e.target.value }))}
                placeholder="Enter comments (optional)"
                className="w-full h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-400"
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowExpectedJoiningDateUpdateModal(false)}
                className="rounded-xl border-gray-200 w-full sm:w-auto order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await handleUpdateExpectedJoiningDate()
                  setShowExpectedJoiningDateUpdateModal(false)
                  setStatusUpdateData({ applicationId: "", newStatus: "", comments: "", selectedJobId: "", currentStatus: "" })
                }}
                disabled={!expectedJoiningDate}
                className="rounded-xl bg-orange-600 text-white hover:bg-orange-700 w-full sm:w-auto order-1 sm:order-2"
              >
                Update Expected Joining Date
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showChangeCompanyModal}
        onOpenChange={(open) => {
          if (!open && !isChangingCompany) {
            setShowChangeCompanyModal(false)
            setChangeCompanyData({
              applicationId: "",
              selectedJobId: "",
              selectedJobLabel: "",
              comments: "",
              candidateName: "",
              currentCompany: "",
            })
          }
        }}
      >
        <DialogContent
          className="w-[95vw] max-w-md rounded-2xl bg-white"
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement | null
            if (
              target?.closest("[data-radix-popper-content-wrapper]") ||
              target?.closest("[cmdk-root]") ||
              target?.closest("[data-radix-select-content]")
            ) {
              e.preventDefault()
            }
          }}
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement | null
            if (
              target?.closest("[data-radix-popper-content-wrapper]") ||
              target?.closest("[cmdk-root]") ||
              target?.closest("[data-radix-select-content]")
            ) {
              e.preventDefault()
            }
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault()
            document.body.style.removeProperty("pointer-events")
            document.body.removeAttribute("data-scroll-locked")
          }}
        >
          <DialogHeader>
            <DialogTitle>Change Company</DialogTitle>
            <DialogDescription>
              Correct the job/company on the same application for{" "}
              <span className="font-medium text-foreground">{changeCompanyData.candidateName || "this candidate"}</span>
              {changeCompanyData.currentCompany ? (
                <>
                  {" "}
                  (currently <span className="font-medium text-foreground">{changeCompanyData.currentCompany}</span>).
                </>
              ) : (
                "."
              )}{" "}
              Status will not change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Select New Job</Label>
              <SearchableSelect
                options={(() => {
                  const published = jobs.filter(
                    (job) => String(job.status || "").toLowerCase() === "published",
                  )
                  const options = published.map((job) => {
                    const id = String(job.id || job._id || "")
                    const title = job.title || "Untitled"
                    const company = job?.companyName || "N/A"
                    const location = job?.location || ""
                    return {
                      value: id,
                      triggerLabel: location
                        ? `${title} · ${company} · ${location}`
                        : `${title} · ${company}`,
                      label: (
                        <div className="text-left space-y-0.5">
                          <div className="font-medium text-sm text-foreground leading-tight">{title}</div>
                          <div className="text-xs text-muted-foreground leading-tight">
                            {[company, location].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      ),
                      searchText: `${title} ${company} ${location}`,
                    }
                  }).filter((o) => o.value)
                  // Keep selected option visible even if it fell out of the current search page
                  if (
                    changeCompanyData.selectedJobId &&
                    !options.some((o) => o.value === changeCompanyData.selectedJobId) &&
                    changeCompanyData.selectedJobLabel
                  ) {
                    options.unshift({
                      value: changeCompanyData.selectedJobId,
                      triggerLabel: changeCompanyData.selectedJobLabel,
                      label: (
                        <div className="text-left">
                          <div className="font-medium text-sm">{changeCompanyData.selectedJobLabel}</div>
                        </div>
                      ),
                      searchText: changeCompanyData.selectedJobLabel,
                    })
                  }
                  return options
                })()}
                value={changeCompanyData.selectedJobId}
                onValueChange={(value) => {
                  const job = jobs.find((j) => String(j.id || j._id || "") === value)
                  const title = job?.title || "Untitled"
                  const company = job?.companyName || "N/A"
                  const location = job?.location || ""
                  setChangeCompanyData((prev) => ({
                    ...prev,
                    selectedJobId: value,
                    selectedJobLabel: job
                      ? location
                        ? `${title} · ${company} · ${location}`
                        : `${title} · ${company}`
                      : prev.selectedJobLabel,
                  }))
                }}
                onSearchChange={debouncedJobSearch}
                placeholder="Search and select a job..."
                searchPlaceholder="Search jobs..."
                emptyText="No jobs available"
                className="w-full mt-1 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 h-10"
                loading={isLoadingJobs && !changeCompanyData.selectedJobId}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">
                Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={changeCompanyData.comments}
                onChange={(e) =>
                  setChangeCompanyData((prev) => ({ ...prev, comments: e.target.value }))
                }
                placeholder="Why is the company being corrected?"
                className="w-full mt-1 h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-400"
                disabled={isChangingCompany}
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
              <Button
                variant="outline"
                disabled={isChangingCompany}
                onClick={() => setShowChangeCompanyModal(false)}
                className="rounded-xl border-gray-200 w-full sm:w-auto order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button
                disabled={
                  isChangingCompany ||
                  !changeCompanyData.selectedJobId ||
                  !changeCompanyData.comments.trim()
                }
                onClick={async () => {
                  if (!changeCompanyData.applicationId || !changeCompanyData.selectedJobId) return
                  const reason = changeCompanyData.comments.trim()
                  if (!reason) {
                    toast.error("Reason is required")
                    return
                  }
                  setIsChangingCompany(true)
                  try {
                    await editApplication(changeCompanyData.applicationId, {
                      jobId: changeCompanyData.selectedJobId,
                      jobType: "main",
                      comments: reason,
                    })
                    setShowChangeCompanyModal(false)
                    setChangeCompanyData({
                      applicationId: "",
                      selectedJobId: "",
                      selectedJobLabel: "",
                      comments: "",
                      candidateName: "",
                      currentCompany: "",
                    })
                    await fetchApplications()
                  } catch (err) {
                    // toast handled in editApplication
                  } finally {
                    setIsChangingCompany(false)
                  }
                }}
                className="rounded-xl bg-orange-600 text-white hover:bg-orange-700 w-full sm:w-auto order-1 sm:order-2"
              >
                {isChangingCompany ? "Saving..." : "Change Company"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showEditCandidateModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowEditCandidateModal(false)
            setEditCandidateApp(null)
            setEditCandidateForm({ name: "", email: "", phone: "" })
          }
        }}
      >
        <DialogContent className="w-[95vw] max-w-md rounded-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Edit candidate
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="rounded-full border border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600"
                    >
                      i
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-left text-xs">
                    Change how this candidate appears in the system. Old and new values, your user id, and time are stored
                    in candidateEditHistory. Email and phone must stay unique across candidates.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DialogTitle>
            <DialogDescription>Edit name, email, and phone for this applicant&apos;s candidate record.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="va-edit-candidate-name">Name *</Label>
              <Input
                id="va-edit-candidate-name"
                value={editCandidateForm.name}
                onChange={(e) => {
                  setEditCandidateForm((f) => ({ ...f, name: e.target.value }))
                  setEditCandidateFieldErrors((prev) => ({ ...prev, name: "" }))
                }}
              />
              {editCandidateFieldErrors.name && (
                <p className="text-xs text-red-600">{editCandidateFieldErrors.name}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="va-edit-candidate-email">Email *</Label>
              <Input
                id="va-edit-candidate-email"
                type="email"
                value={editCandidateForm.email}
                onChange={(e) => {
                  setEditCandidateForm((f) => ({ ...f, email: e.target.value }))
                  setEditCandidateFieldErrors((prev) => ({ ...prev, email: "" }))
                }}
              />
              {editCandidateFieldErrors.email && (
                <p className="text-xs text-red-600">{editCandidateFieldErrors.email}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="va-edit-candidate-phone">Phone *</Label>
              <Input
                id="va-edit-candidate-phone"
                value={editCandidateForm.phone}
                inputMode="numeric"
                maxLength={10}
                onChange={(e) =>
                  {
                    setEditCandidateForm((f) => ({
                      ...f,
                      phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                    }))
                    setEditCandidateFieldErrors((prev) => ({ ...prev, phone: "" }))
                  }
                }
              />
              {editCandidateFieldErrors.phone && (
                <p className="text-xs text-red-600">{editCandidateFieldErrors.phone}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={editCandidateSaving}
              onClick={() => {
                setShowEditCandidateModal(false)
                setEditCandidateApp(null)
                setEditCandidateForm({ name: "", email: "", phone: "" })
                setEditCandidateFieldErrors({ name: "", email: "", phone: "" })
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-orange-600 hover:bg-orange-700"
              disabled={editCandidateSaving}
              onClick={async () => {
                if (!editCandidateApp?.candidate?.id) return
                const payload = {
                  name: editCandidateForm.name.trim(),
                  email: editCandidateForm.email.trim(),
                  phone: editCandidateForm.phone.trim(),
                }
                const nextErrors = {
                  name: payload.name ? "" : "Name is required.",
                  email: payload.email ? "" : "Email is required.",
                  phone: payload.phone ? "" : "Phone is required.",
                }
                setEditCandidateFieldErrors(nextErrors)
                if (nextErrors.name || nextErrors.email || nextErrors.phone) {
                  toast.error("Please fill all required fields.")
                  return
                }
                setEditCandidateSaving(true)
                try {
                  await updateCandidateContactDetails(editCandidateApp.candidate.id, payload)
                  setShowEditCandidateModal(false)
                  setEditCandidateApp(null)
                  await fetchApplications()
                  toast.success("Candidate contact updated successfully.")
                } catch (err: any) {
                  const msg =
                    err?.response?.data?.message ||
                    err?.message ||
                    "Failed to update candidate contact."
                  toast.error(msg)
                } finally {
                  setEditCandidateSaving(false)
                }
              }}
            >
              {editCandidateSaving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Interview Details Modal */}
      <InterviewDetailsModal
        isOpen={showInterviewModal}
        onClose={() => {
          setShowInterviewModal(false)
          setShowLoaderInInterviewPopup(false)
        }}
        selectedApplication={selectedApplication}
        interviewDetails={interviewDetails}
        setInterviewDetails={setInterviewDetails}
        showScheduleForm={showScheduleForm}
        setShowScheduleForm={setShowScheduleForm}
        scheduleFormData={scheduleFormData}
        setScheduleFormData={setScheduleFormData}
        validationErrors={validationErrors}
        setValidationErrors={setValidationErrors}
        emailInput={emailInput}
        setEmailInput={setEmailInput}
        currentRoundForAction={currentRoundForAction}
        setCurrentRoundForAction={setCurrentRoundForAction}
        user={user ? { ...user, username: user.username ?? "" } : null}
        isScheduling={isScheduling}
        onScheduleSubmit={handleSubmitSchedule}
        onResultRound={handleResultRound}
        onFetchJobLocation={fetchAndPopulateJobLocation}
        showLoader={showLoaderInInterviewPopup}
        setShowLoader={setShowLoaderInInterviewPopup}
      />

      {/* Result Submission Modal */}
      <ResultModal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        resultFormData={resultFormData}
        setResultFormData={setResultFormData}
        isSubmittingResult={isSubmittingResult}
        onSubmit={handleSubmitResult}
      />

      {/* Applications Table */}
      <div className="flex-1 min-h-0 overflow-auto bg-background">
        {/* Desktop Table View */}
        <div className="hidden lg:block min-w-full">
          <table className="min-w-full divide-y divide-border">
            <thead style={{ backgroundColor: "#FFF5F2" }}>
              <tr>
                <th className="px-4 py-3 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
                <SortableHeader column="candidate.name" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-border">
                  Candidate Name
                </SortableHeader>
                 <SortableHeader column="company.companyName" className="min-w-48 bg-[#FFF5F2] border-r border-border">
                  Company Name
                </SortableHeader>
                 <SortableHeader column="company.companyLocation" className="min-w-48 bg-[#FFF5F2] border-r border-border">
                  Company Location
                </SortableHeader>
                <SortableHeader column="candidate.email" className="bg-[#FFF5F2] min-w-64">Email</SortableHeader>
                <SortableHeader column="candidate.phone" className="bg-[#FFF5F2] min-w-32">Phone</SortableHeader>
                <SortableHeader column="currentStatus.status" className="bg-[#FFF5F2] min-w-48">Status</SortableHeader>
                <SortableHeader column="sourcedBy.username" className="bg-[#FFF5F2] min-w-48">Sourced By</SortableHeader>
                <SortableHeader column="interviewDate" className="bg-[#FFF5F2] min-w-40">Applied On</SortableHeader>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Interview Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Interview
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-border">
              {isLoading ? (
                <>
                  {[...Array(10)].map((_, index) => (
                    <tr key={`shimmer-${index}`} className="bg-background divide-y divide-border">
                      {/* Empty column */}
                      <td className="px-4 py-4"></td>
                      {/* Candidate Name */}
                      <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border">
                        <div className="flex items-center gap-3">
                          <div className="animate-pulse bg-gray-200 rounded-full w-8 h-8"></div>
                          <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                        </div>
                      </td>
                      {/* Company Name */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-40 h-4"></div>
                      </td>
                      {/* Company Location */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-48 h-4"></div>
                      </td>
                      {/* Email */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-56 h-4"></div>
                      </td>
                      {/* Phone */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                      </td>
                      {/* Status */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded-full w-20 h-6"></div>
                      </td>
                      {/* Sourced By */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Applied On */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-28 h-4"></div>
                      </td>
                      {/* Interview Date */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Interview */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-16 h-4"></div>
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-16 h-6"></div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <p className="text-muted-foreground">{error}</p>
                  </td>
                </tr>
              ) : applications.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <p className="text-muted-foreground">No applications found matching your criteria.</p>
                  </td>
                </tr>
              ) : (
                applications.map((app) => (
                  <tr
                    key={app.applicationId}
                    className={cn("hover:bg-muted/30 transition-colors cursor-pointer")}
                    onClick={() => handleCandidateNameClick(app)}
                  >
                    <td className="px-4 py-4"></td>
                    <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border relative group">
                      <div className="flex items-center gap-3">
                        <span className="font-medium transition-colors cursor-pointer text-foreground hover:text-primary">
                          {app.candidate.name}
                        </span>
                      </div>
                      <div className="absolute top-0 left-[calc(100%+0.5rem)] mt-1 w-80 p-4 bg-white shadow-lg rounded border border-border opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto z-30">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-lg">{app.candidate.name}</h4>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="secondary" className="text-xs">
                              {app.currentStatus?.status || "N/A"}
                            </Badge>
                            {(user?.role === "ADMIN" || user?.role === "SHM") && app.candidate?.id && (
                              <button
                                type="button"
                                className="inline-flex h-5 w-5 items-center justify-center rounded border border-orange-200 text-orange-600 hover:bg-orange-50"
                                title="Edit candidate contact"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setEditCandidateApp(app)
                                  setEditCandidateForm({
                                    name: app.candidate.name || "",
                                    email: app.candidate.email || "",
                                    phone: app.candidate.phone || "",
                                  })
                                  setEditCandidateFieldErrors({ name: "", email: "", phone: "" })
                                  setShowEditCandidateModal(true)
                                }}
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Job:</span>
                            <span className="font-medium">{app.job?.title || "N/A"}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Location:</span>
                            <div className="max-w-48 truncate" title={app.candidate.currentLocationDetails?.fullAddress}>
                              {user?.role === "INVESTOR" ? `${app.candidate.currentLocationDetails?.fullAddress.slice(0, 2)}xxxxxx${app.candidate.currentLocationDetails?.fullAddress.slice(-2)}` : app.candidate.currentLocationDetails?.fullAddress || "N/A"}
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Phone:</span>
                            <span className="font-medium">{user?.role === "INVESTOR" ? `${app.candidate.phone.slice(0, 2)}xxxxxx${app.candidate.phone.slice(-2)}` : app.candidate.phone}</span>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-border">
                          <div className="text-xs text-muted-foreground">
                            Applied {new Date(app.appliedOn).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span>{toDisplayText(app?.company.companyName)}</span>
                        {app.companyCorrected && (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1.5 py-0 h-5 border-amber-300 bg-amber-50 text-amber-800 font-medium"
                                >
                                  Changed
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="font-medium">Company corrected by Admin</p>
                                {app.companyCorrectedAt && (
                                  <p className="text-xs opacity-90 mt-0.5">
                                    {format(new Date(app.companyCorrectedAt), "dd MMM yyyy")}
                                  </p>
                                )}
                                {app.companyCorrectedReason && (
                                  <p className="text-xs mt-1.5 whitespace-pre-wrap">
                                    Reason: {app.companyCorrectedReason}
                                  </p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground truncate max-w-48">{toDisplayText(app?.company.companyLocation)}</td>

                    <td className="px-4 py-4 text-sm text-foreground">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy email"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard.writeText(app.candidate.email)
                          toast.success("Email copied!")
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "underline"
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = "none"
                        }}
                      >
                        {user?.role === "INVESTOR" ? `${app.candidate.email.slice(0, 4)}xxxxxx${app.candidate.email.slice(-4)}` : app.candidate.email}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy phone number"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard.writeText(app.candidate.phone)
                          toast.success("Phone number copied!")
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "underline"
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = "none"
                        }}
                      >
                        {user?.role === "INVESTOR" ? `${app.candidate.phone.slice(0, 2)}xxxxxx${app.candidate.phone.slice(-2)}` : app.candidate.phone}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <Badge className={getStatusBadgeColor(app.currentStatus?.status)}>
                        {app.currentStatus?.status || "N/A"}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">{app.sourcedBy?.username || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">
                      {getInterviewDate(app, interviewDetails) || "N/A"}
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">
                      <p>
                        {app.interview?.date
                          ? new Date(app.interview.date).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "N/A"}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <Button
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleViewInterviewDetails(app)
                          toast.dismiss()
                        }}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 rounded-lg px-3 py-1.5 border border-orange-300 text-orange-600 font-medium bg-white shadow-sm hover:bg-orange-50 hover:shadow-md hover:border-orange-400 active:scale-95 transition-all duration-200"
                        disabled={app.currentStatus?.status === "Joined"}
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                    </td>
                    <td className="px-4 py-4">{renderActionsDropdown(app)}

                    
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden">
          <div className="space-y-2 sm:space-y-3 p-2 sm:p-4">
            {isLoading ? (
              <>
                {[...Array(5)].map((_, index) => (
                  <div key={`mobile-shimmer-${index}`} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="animate-pulse space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="bg-gray-200 rounded-full w-10 h-10"></div>
                        <div className="bg-gray-200 rounded w-32 h-4"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="bg-gray-200 rounded w-full h-3"></div>
                        <div className="bg-gray-200 rounded w-3/4 h-3"></div>
                        <div className="bg-gray-200 rounded w-1/2 h-3"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : error ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <p className="text-muted-foreground">{error}</p>
              </div>
            ) : applications.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <p className="text-muted-foreground">No applications found matching your criteria.</p>
              </div>
            ) : (
              applications.map((app) => (
                <div
                  key={app.applicationId}
                  className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleCandidateNameClick(app)}
                >
                  <div className="flex items-start justify-between mb-2 sm:mb-3">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <div className="h-8 w-8 sm:h-10 sm:w-10 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm flex-shrink-0">
                        {app.candidate.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{app.candidate.name}</h3>
                        <p className="text-xs sm:text-sm text-gray-500 truncate flex items-center gap-1">
                          <span className="truncate">{app?.company.companyName}</span>
                          {app.companyCorrected && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1 py-0 h-4 border-amber-300 bg-amber-50 text-amber-800 shrink-0"
                              title={[
                                "Company corrected by Admin",
                                app.companyCorrectedAt
                                  ? format(new Date(app.companyCorrectedAt), "dd MMM yyyy")
                                  : "",
                                app.companyCorrectedReason
                                  ? `Reason: ${app.companyCorrectedReason}`
                                  : "",
                              ]
                                .filter(Boolean)
                                .join("\n")}
                            >
                              Changed
                            </Badge>
                          )}
                        </p>
                      </div>
                    </div>
                    <Badge className={`${getStatusBadgeColor(app.currentStatus?.status)} text-xs px-2 py-1 flex-shrink-0`}>
                      {app.currentStatus?.status || "N/A"}
                    </Badge>
                  </div>
                  
                  <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                    <div className="flex items-center gap-2 text-xs sm:text-sm">
                      <Mail className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-600 truncate">{app.candidate.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm">
                      <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-600">{app.candidate.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm">
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-600 truncate">{app?.company.companyLocation}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm">
                      <User className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-600 truncate">Sourced by: {app.sourcedBy?.username || "N/A"}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-gray-100">
                    <div className="text-xs text-gray-500">
                      Applied: {getInterviewDate(app, interviewDetails) || "N/A"}
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Button
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleViewInterviewDetails(app)
                          toast.dismiss()
                        }}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1 rounded-lg px-2 py-1 border border-orange-300 text-orange-600 font-medium bg-white hover:bg-orange-50 hover:border-orange-400 transition-all duration-200 text-xs h-7"
                        disabled={app.currentStatus?.status === "Joined"}
                      >
                        <Eye className="h-3 w-3" />
                        <span className="hidden sm:inline">View</span>
                      </Button>
                      <div onClick={(e) => e.stopPropagation()}>
                        {renderActionsDropdown(app)}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3 p-2 sm:p-4 pl-2 sm:pl-8 flex-shrink-0 border-t border-gray-200 bg-white">
          <div className="text-xs sm:text-sm text-muted-foreground order-2 sm:order-1">
            Showing {applications.length} of {pagination.totalResults} Interview applications
          </div>
          <div className="flex items-center gap-1 sm:gap-2 order-1 sm:order-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50 h-7 w-7 sm:h-8 sm:w-8 p-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-1">
            {(() => {
              const { page, totalPages } = pagination
              const delta = 2
              let startPage = Math.max(1, page - delta)
              let endPage = Math.min(totalPages, page + delta)

              const pageButton = (pageNum: number) => (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  disabled={isLoading}
                  className={
                    page === pageNum
                      ? "bg-[#EC6E51] text-white hover:bg-orange-700 h-7 w-7 sm:h-8 sm:w-8 p-0"
                      : "border-gray-200 hover:bg-orange-50 h-7 w-7 sm:h-8 sm:w-8 p-0"
                  }
                >
                  {pageNum}
                </Button>
              )

              const items: ReactNode[] = []

              if (startPage > 1) {
                items.push(pageButton(1))
                if (startPage > 2) {
                  items.push(
                    <span key="ellipsis-start" className="text-sm text-muted-foreground px-1">
                      ...
                    </span>
                  )
                }
              }

              for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
                items.push(pageButton(pageNum))
              }

              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  items.push(
                    <span key="ellipsis-end" className="text-sm text-muted-foreground px-1">
                      ...
                    </span>
                  )
                }
                items.push(pageButton(totalPages))
              }

              return items
            })()}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50 h-7 w-7 sm:h-8 sm:w-8 p-0"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

    </div>
  )
}

export default function ViewApplications({ onAddCandidate }: { onAddCandidate?: () => void }) {
  return <ApplicationsList />
}